import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Store, Settings, MapPin } from 'lucide-react';
import type { LocationFormProps, LocationFormData } from './LocationForm.types';
import type { LocationFormErrors } from '../../types';

const DIAS_SEMANA = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
] as const;

export const LocationForm: React.FC<LocationFormProps> = ({
  location,
  onSubmit,
  onCancel,
  isLoading = false,
  isEdit = false,
  error,
}) => {
  const [formData, setFormData] = useState<LocationFormData>({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    tipoLocal: 'SUCURSAL',
    fechaApertura: new Date().toISOString().split('T')[0],
    serieBoleta: '',
    serieFactura: '',
    serieBoletaManual: '',
    configuracion: {
      aceptaEfectivo: true,
      aceptaTarjeta: true,
      aceptaYape: true,
      tieneDelivery: true,
      tieneRecojo: true,
    },
    horarios: {
      lunes: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
      martes: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
      miercoles: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
      jueves: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
      viernes: { abierto: true, horaApertura: '08:00', horaCierre: '23:00' },
      sabado: { abierto: true, horaApertura: '09:00', horaCierre: '23:00' },
      domingo: { abierto: true, horaApertura: '09:00', horaCierre: '21:00' },
    },
  });

  const [errors, setErrors] = useState<LocationFormErrors>({});

  // Cargar datos si está editando
  useEffect(() => {
    if (isEdit && location) {
      // Asegurar que horarios tenga la estructura correcta
      const defaultHorarios = {
        lunes: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        martes: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        miercoles: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        jueves: { abierto: true, horaApertura: '08:00', horaCierre: '22:00' },
        viernes: { abierto: true, horaApertura: '08:00', horaCierre: '23:00' },
        sabado: { abierto: true, horaApertura: '09:00', horaCierre: '23:00' },
        domingo: { abierto: true, horaApertura: '09:00', horaCierre: '21:00' },
      };

      // Merge horarios existentes con defaults para asegurar que todas las propiedades existan
      const mergedHorarios = { ...defaultHorarios };
      if (location.horarios) {
        Object.keys(location.horarios).forEach(dia => {
          if (mergedHorarios[dia as keyof typeof mergedHorarios]) {
            mergedHorarios[dia as keyof typeof mergedHorarios] = {
              ...mergedHorarios[dia as keyof typeof mergedHorarios],
              ...location.horarios[dia]
            };
          }
        });
      }

      setFormData({
        nombre: location.nombre,
        direccion: location.direccion,
        telefono: location.telefono,
        email: location.email,
        tipoLocal: location.tipoLocal,
        fechaApertura: location.fechaApertura,
        serieBoleta: location.serieBoleta || '',
        serieFactura: location.serieFactura || '',
        serieBoletaManual: location.serieBoletaManual || '',
        configuracion: location.configuracion || {
          aceptaEfectivo: true,
          aceptaTarjeta: true,
          aceptaYape: true,
          tieneDelivery: true,
          tieneRecojo: true,
        },
        horarios: mergedHorarios,
      });
    }
  }, [isEdit, location]);

  const validateForm = (): boolean => {
    const newErrors: LocationFormErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.direccion.trim()) {
      newErrors.direccion = 'La dirección es requerida';
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = 'El teléfono es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }


    if (!formData.fechaApertura) {
      newErrors.fechaApertura = 'La fecha de apertura es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEdit && location) {
        await onSubmit({
          id: location.id,
          ...formData,
        });
      } else {
        await onSubmit(formData);
      }
    } catch (error) {
      console.error('Error al guardar sucursal:', error);
    }
  };

  const handleInputChange = (field: keyof LocationFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Limpiar error si existe
    if (errors[field as keyof LocationFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleConfiguracionChange = (field: keyof LocationFormData['configuracion']) => (checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      configuracion: {
        ...prev.configuracion,
        [field]: checked,
      },
    }));
  };

  const handleHorarioChange = (dia: keyof LocationFormData['horarios'], field: 'abierto' | 'horaApertura' | 'horaCierre' | 'horaAperturaTarde' | 'horaCierreTarde') => (
    value: boolean | string
  ) => {
    setFormData(prev => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: {
          ...prev.horarios[dia],
          [field]: value,
        },
      },
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="general">Información General</TabsTrigger>
          <TabsTrigger value="horarios">Horarios de Atención</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="space-y-6 mt-6">
          {/* Información básica */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Información Básica
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombre">Nombre de la Sucursal *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange('nombre')}
                  placeholder="Ej: Macartur Miraflores"
                  className={errors.nombre ? 'border-red-500' : ''}
                />
                {errors.nombre && <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>}
              </div>

              <div>
                <Label htmlFor="tipoLocal">Tipo de Local</Label>
                <Select
                  value={formData.tipoLocal}
                  onValueChange={(value: 'PRINCIPAL' | 'SUCURSAL') => {
                    setFormData(prev => ({
                      ...prev,
                      tipoLocal: value,
                    }));
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Seleccionar tipo de local" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRINCIPAL">Principal</SelectItem>
                    <SelectItem value="SUCURSAL">Sucursal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="direccion">Dirección *</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange('direccion')}
                  placeholder="Av. Principal 123, Distrito"
                  className={errors.direccion ? 'border-red-500' : ''}
                />
                {errors.direccion && <p className="text-red-500 text-sm mt-1">{errors.direccion}</p>}
              </div>

              <div>
                <Label htmlFor="telefono">Teléfono *</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange('telefono')}
                  placeholder="+51 1 234-5678"
                  className={errors.telefono ? 'border-red-500' : ''}
                />
                {errors.telefono && <p className="text-red-500 text-sm mt-1">{errors.telefono}</p>}
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  placeholder="sucursal@macartur.pe"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="fechaApertura">Fecha de Apertura *</Label>
                <Input
                  id="fechaApertura"
                  type="date"
                  value={formData.fechaApertura}
                  onChange={handleInputChange('fechaApertura')}
                  className={errors.fechaApertura ? 'border-red-500' : ''}
                />
                {errors.fechaApertura && <p className="text-red-500 text-sm mt-1">{errors.fechaApertura}</p>}
              </div>
            </CardContent>
          </Card>

          {/* Configuración */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuración
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aceptaEfectivo"
                    checked={formData.configuracion.aceptaEfectivo}
                    onCheckedChange={handleConfiguracionChange('aceptaEfectivo')}
                  />
                  <Label htmlFor="aceptaEfectivo">Acepta Efectivo</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aceptaTarjeta"
                    checked={formData.configuracion.aceptaTarjeta}
                    onCheckedChange={handleConfiguracionChange('aceptaTarjeta')}
                  />
                  <Label htmlFor="aceptaTarjeta">Acepta Tarjeta</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aceptaYape"
                    checked={formData.configuracion.aceptaYape}
                    onCheckedChange={handleConfiguracionChange('aceptaYape')}
                  />
                  <Label htmlFor="aceptaYape">Acepta Yape</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tieneDelivery"
                    checked={formData.configuracion.tieneDelivery}
                    onCheckedChange={handleConfiguracionChange('tieneDelivery')}
                  />
                  <Label htmlFor="tieneDelivery">Delivery</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Facturación Electrónica */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Facturación Electrónica
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="serieBoleta">Serie para Boleta</Label>
                  <Input
                    id="serieBoleta"
                    value={formData.serieBoleta || ''}
                    onChange={handleInputChange('serieBoleta')}
                    placeholder="B001"
                  />
                </div>

                <div>
                  <Label htmlFor="serieFactura">Serie para Factura</Label>
                  <Input
                    id="serieFactura"
                    value={formData.serieFactura || ''}
                    onChange={handleInputChange('serieFactura')}
                    placeholder="F001"
                  />
                </div>

                <div>
                  <Label htmlFor="serieBoletaManual">Serie para Boleta Manual</Label>
                  <Input
                    id="serieBoletaManual"
                    value={formData.serieBoletaManual || ''}
                    onChange={handleInputChange('serieBoletaManual')}
                    placeholder="BM001"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="horarios" className="space-y-6 mt-6">
          {/* Horarios */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Horarios de Atención (por turno)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {DIAS_SEMANA.map(({ key, label }) => (
                  <div key={key} className="border rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Checkbox
                        id={`${key}-abierto`}
                        checked={formData.horarios[key]?.abierto || false}
                        onCheckedChange={(checked) => handleHorarioChange(key, 'abierto')(checked)}
                      />
                      <Label htmlFor={`${key}-abierto`} className="font-medium">
                        {label}
                      </Label>
                    </div>

                    {formData.horarios[key]?.abierto && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Turno Mañana</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label htmlFor={`${key}-manana-apertura`} className="text-xs">
                                Apertura
                              </Label>
                              <Input
                                id={`${key}-manana-apertura`}
                                type="time"
                                value={formData.horarios[key]?.horaApertura || ''}
                                onChange={(e) => handleHorarioChange(key, 'horaApertura')(e.target.value)}
                                className="text-sm"
                                placeholder=""
                              />
                            </div>
                            <div>
                              <Label htmlFor={`${key}-manana-cierre`} className="text-xs">
                                Cierre
                              </Label>
                              <Input
                                id={`${key}-manana-cierre`}
                                type="time"
                                value={formData.horarios[key]?.horaCierre || ''}
                                onChange={(e) => handleHorarioChange(key, 'horaCierre')(e.target.value)}
                                className="text-sm"
                                placeholder=""
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Turno Tarde/Noche</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label htmlFor={`${key}-tarde-apertura`} className="text-xs">
                                Apertura
                              </Label>
                              <Input
                                id={`${key}-tarde-apertura`}
                                type="time"
                                value={formData.horarios[key]?.horaAperturaTarde || ''}
                                onChange={(e) => handleHorarioChange(key, 'horaAperturaTarde')(e.target.value)}
                                className="text-sm"
                                placeholder=""
                              />
                            </div>
                            <div>
                              <Label htmlFor={`${key}-tarde-cierre`} className="text-xs">
                                Cierre
                              </Label>
                              <Input
                                id={`${key}-tarde-cierre`}
                                type="time"
                                value={formData.horarios[key]?.horaCierreTarde || ''}
                                onChange={(e) => handleHorarioChange(key, 'horaCierreTarde')(e.target.value)}
                                className="text-sm"
                                placeholder=""
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {!formData.horarios[key]?.abierto && (
                      <div className="text-center py-2">
                        <Badge variant="outline" className="text-gray-500">
                          Cerrado
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Error del servidor */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 text-red-600">⚠️</div>
            <div>
              <h4 className="font-medium text-red-900">Error</h4>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-4 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear'} Sucursal
        </Button>
      </div>
    </form>
  );
};